import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogActions, Button, Box, Typography,
  CircularProgress, Paper, Divider, Chip
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import config from '../../../../config';

/**
 * Comprehensive Plan Change Modal
 * Shows detailed comparison between current and new plan including:
 * - Plan details (name, price, credits)
 * - Feature comparison
 * - Token/credit differences
 * - Proration details
 * - Billing information
 *
 * User confirms in this modal, then payment is triggered immediately
 */
const PlanChangeModal = ({
  open, onClose, onConfirm, currentPlan, newPlan, subscriptionId, storeId, nextDueDate,
  subscriptionCreditsGranted = 0,
  subscriptionCreditsConsumed = 0,
  topupCreditsGranted = 0,
  topupCreditsConsumed = 0,
  isProcessing = false,
  isSuccess = false,
  invoiceUrl = null,
  successAmountCents = null,

  errorMessage = null,
  numLocations = 1,

  processingMessage = "Processing your upgrade..."
}) => {
  const [loadingProration, setLoadingProration] = useState(false);
  const [prorationData, setProrationData] = useState(null);
  const [fetchError, setFetchError] = useState(null);



  // Helper to get professional heading based on error message
  const getErrorHeading = (error) => {
    if (!error) return "Transaction Failed";
    const msg = error.toLowerCase();
    if (msg.includes("trial")) return "Trial Period Restriction";
    if (msg.includes("24 hours") || msg.includes("settlement")) return "Plan Change Restricted";
    return "Transaction Failed";
  };

  const currentError = errorMessage || fetchError;
  const errorHeading = getErrorHeading(currentError);
  const isBusinessRestriction = currentError && (currentError.toLowerCase().includes("trial") || currentError.toLowerCase().includes("24 hours"));



  // Fetches proration calculation from backend
  const fetchProrationData = React.useCallback(async () => {
    try {
      setLoadingProration(true);
      setFetchError(null);
      const url = `${config.API_BASE_V2_URL}/subscriptions/proration`;
      const payload = {
        "store_id": storeId,
        "subscription_id": subscriptionId,
        "new_plan_id": newPlan.plan_id
      };
      const response = await fetch(url, {
        method: 'POST',
        headers: config.getHeaders(),
        body: JSON.stringify(payload)
      });
      const json = await response.json();
      if (json.success) {
        setProrationData(json);
      } else {
        setFetchError(json.error || 'Failed to calculate proration');
      }
    } catch (err) {
      console.error('Proration fetch error:', err);
      setFetchError('Failed to fetch proration details');
    } finally {
      setLoadingProration(false);
    }
  }, [storeId, subscriptionId, newPlan]);

  // Fetch proration data when modal opens
  useEffect(() => {
    if (open && storeId && currentPlan && newPlan && !isSuccess && !isProcessing) {
      fetchProrationData();
    }
  }, [open, storeId, currentPlan, newPlan, isSuccess, isProcessing, fetchProrationData]);

  const handleConfirmAction = () => {
    if (!prorationData || fetchError || isProcessing || isSuccess) return;
    onConfirm(newPlan, prorationData);
  };



  const tokenDiff = ((newPlan?.tokens || 0) * numLocations) - ((currentPlan?.tokens || 0) * numLocations);

  // PROACTIVE DIRECTION CHECK: Use plan prices to determine direction immediately 
  // rather than waiting for backend proration data, preventing UI "flicker" or wrong defaults.
  const isUpgrade = parseFloat(newPlan?.plan_amount || 0) * numLocations > parseFloat(currentPlan?.plan_amount || 0) * numLocations;
  const isDowngrade = parseFloat(newPlan?.plan_amount || 0) * numLocations < parseFloat(currentPlan?.plan_amount || 0) * numLocations;

  const prorationAmountCents = prorationData?.proration_amount_cents || 0;


  // For downgrades: positive cents means REFUND, negative means CHARGE
  const isRefund = isDowngrade && prorationAmountCents > 0;


  return (
    <Dialog
      open={open}
      onClose={isProcessing ? null : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          overflow: 'hidden'
        }
      }}
    >
      {/* Modern SaaS Header */}
      <Box
        sx={{
          p: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'white',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '12px',
              bgcolor: isSuccess ? 'success.50' : (isUpgrade ? 'primary.50' : 'warning.50'),
              color: isSuccess ? 'success.main' : (isUpgrade ? 'primary.main' : 'warning.main'),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {isSuccess ? <CheckCircleIcon sx={{ fontSize: 28 }} /> : (isUpgrade ? <TrendingUpIcon sx={{ fontSize: 28 }} /> : <TrendingDownIcon sx={{ fontSize: 28 }} />)}
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ lineHeight: 1.2 }}>
              {isSuccess ? "Plan Changed Successfully!" : (isUpgrade ? "Upgrade to " + newPlan?.plan_name : "Downgrade to " + newPlan?.plan_name)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isSuccess ? `You are now on the ${newPlan?.plan_name} plan.` : "Review your new billing cycle and prorated details"}
            </Typography>

          </Box>
        </Box>
        <Chip
          label={isSuccess ? "COMPLETED" : (isUpgrade ? "UPGRADE" : "PLAN CHANGE")}
          size="small"
          sx={{
            bgcolor: isSuccess ? 'success.50' : (isUpgrade ? 'primary.50' : 'warning.50'),
            color: isSuccess ? 'success.main' : (isUpgrade ? 'primary.main' : 'warning.main'),
            fontWeight: 700,
            fontSize: '11px',
            borderRadius: '6px',
            border: '1px solid',
            borderColor: isSuccess ? 'success.100' : (isUpgrade ? 'primary.100' : 'warning.100')
          }}
        />
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {isProcessing || loadingProration ? (
          <Box textAlign="center" py={12} px={4}>
            <CircularProgress size={60} thickness={4} sx={{ color: '#605DFF' }} />
            <Typography variant="h6" fontWeight={700} mt={4}>
              {loadingProration ? "Calculating proration..." : processingMessage}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              {loadingProration ? "Fetching your updated billing details..." : "Please do not close this window or refresh the page."}
            </Typography>
          </Box>
        ) : isSuccess ? (

          <Box textAlign="center" py={8} px={6}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: 'success.50',
                color: 'success.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px'
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 48 }} />
            </Box>
            <Typography variant="h5" fontWeight={800} gutterBottom>
              Plan Updated Successfully!
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={4}>
              {processingMessage || `Your account has been moved to the ${newPlan?.plan_name} plan. ${tokenDiff > 0 ? `Your new allowance of ${(newPlan?.tokens * numLocations)?.toLocaleString()} credits is now active.` : (tokenDiff < 0 ? `Your subscription has been updated to ${(newPlan?.tokens * numLocations)?.toLocaleString()} credits.` : '')}`}
            </Typography>



            <Paper variant="outlined" sx={{ p: 3, borderRadius: '16px', bgcolor: '#F8FAFC', mb: 4 }}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="text.secondary">{isRefund ? "Refund Amount" : "Transaction Amount"}</Typography>
                <Typography variant="body2" fontWeight={700}>
                  ${(successAmountCents !== null ? Math.abs(successAmountCents / 100) : Math.abs(prorationAmountCents / 100)).toFixed(2)}
                </Typography>
              </Box>


              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="text.secondary">New Plan</Typography>
                <Typography variant="body2" fontWeight={700}>{newPlan?.plan_name}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Credits Granted</Typography>
                <Typography variant="body2" fontWeight={700}>{(newPlan?.tokens * numLocations)?.toLocaleString()}</Typography>
              </Box>
            </Paper>

            {invoiceUrl && (
              <Button
                variant="outlined"
                fullWidth
                size="large"
                href={invoiceUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  borderRadius: '12px',
                  py: 1.5,
                  fontWeight: 700,
                  borderColor: 'divider',
                  color: 'text.primary'
                }}
              >
                View Transaction Receipt (PDF)
              </Button>

            )}
          </Box>
        ) : fetchError || errorMessage ? (
          <Box py={8} px={4} textAlign="center">
            {isBusinessRestriction ? (
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: isUpgrade ? 'primary.50' : 'warning.50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px'
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 48, color: isUpgrade ? 'primary.main' : 'warning.main' }} />
              </Box>
            ) : (
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: 'error.50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px'
                }}
              >
                <CancelIcon sx={{ fontSize: 48, color: 'error.main' }} />
              </Box>
            )}

            <Typography color={isBusinessRestriction ? (isUpgrade ? "primary.main" : "warning.main") : "error"} variant="h5" fontWeight={800} sx={{ letterSpacing: '-0.5px' }}>
              {errorHeading}
            </Typography>

            <Typography color="text.secondary" mt={2} sx={{ maxWidth: 440, margin: '16px auto', fontSize: '1.05rem', lineHeight: 1.6 }}>
              {currentError}
            </Typography>

            {!isBusinessRestriction && (
              <Box mt={4} display="flex" justifyContent="center" gap={2}>
                {fetchError && (
                  <Button
                    variant="contained"
                    onClick={fetchProrationData}
                    sx={{
                      borderRadius: '12px',
                      px: 4,
                      py: 1.5,
                      fontWeight: 700,
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    Try Again
                  </Button>
                )}
                <Button
                  variant={fetchError ? "outlined" : "contained"}
                  onClick={onClose}
                  sx={{
                    borderRadius: '12px',
                    px: 4,
                    py: 1.5,
                    fontWeight: 700,
                    bgcolor: fetchError ? 'transparent' : 'error.main',
                    '&:hover': {
                      bgcolor: fetchError ? 'rgba(0,0,0,0.04)' : 'error.dark',
                    }
                  }}
                >
                  {fetchError ? "Cancel" : "Got it, Thanks"}
                </Button>
              </Box>
            )}
          </Box>

        ) : (
          <Box sx={{ display: 'flex', height: '100%', minHeight: '440px' }}>
            {/* Left Column: Plan & Features (60%) */}
            <Box sx={{ flex: '1.2', p: 3, borderRight: '1px solid rgba(0,0,0,0.06)' }}>
              <Typography variant="overline" color="text.secondary" fontWeight={700} sx={{ mb: 2, display: 'block', letterSpacing: '1px' }}>
                PLAN COMPARISON
              </Typography>

              <Box display="flex" gap={2} mb={4}>
                <Paper variant="outlined" sx={{ flex: 1, p: 2, borderRadius: '12px', bgcolor: '#F8FAFC', borderStyle: 'dashed' }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>FROM</Typography>
                  <Typography variant="subtitle1" fontWeight={700} color="text.primary" sx={{ mb: 0.5 }}>
                    {currentPlan?.plan_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">${(currentPlan?.plan_amount * numLocations).toFixed(2)}/mo</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    (${currentPlan?.plan_amount} × {numLocations} locs)
                  </Typography>
                </Paper>

                <Box display="flex" alignItems="center">
                  <TrendingUpIcon sx={{ color: 'divider' }} />
                </Box>

                <Paper
                  variant="outlined"
                  sx={{
                    flex: 1,
                    p: 2,
                    borderRadius: '12px',
                    borderColor: isUpgrade ? 'primary.200' : 'warning.200',
                    bgcolor: isUpgrade ? 'primary.50' : 'warning.50',
                    boxShadow: isUpgrade ? '0 4px 6px -1px rgba(96, 93, 255, 0.1)' : 'none'
                  }}
                >
                  <Typography variant="caption" color={isUpgrade ? 'primary.main' : 'warning.main'} fontWeight={600}>TO</Typography>
                  <Typography variant="subtitle1" fontWeight={700} color="text.primary" sx={{ mb: 0.5 }}>
                    {newPlan?.plan_name}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>${(newPlan?.plan_amount * numLocations).toFixed(2)}/mo</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    (${newPlan?.plan_amount} × {numLocations} locs)
                  </Typography>
                </Paper>
              </Box>

              <Typography variant="overline" color="text.secondary" fontWeight={700} sx={{ mb: 2, display: 'block', letterSpacing: '1px' }}>
                WHATS CHANGING
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: '12px', bgcolor: '#F8FAFC', border: '1px solid rgba(0,0,0,0.05)' }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography variant="body2" fontWeight={700}>Credits</Typography>
                    <Chip
                      label={tokenDiff > 0 ? `+${tokenDiff.toLocaleString()}` : tokenDiff.toLocaleString()}
                      size="small"
                      color={tokenDiff > 0 ? "success" : "warning"}
                      sx={{ height: 18, fontSize: '10px', fontWeight: 700, borderRadius: '4px' }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                    New allocation of {(newPlan?.tokens * numLocations)?.toLocaleString()} credits per month ({newPlan?.tokens?.toLocaleString()} per location).
                  </Typography>
                </Paper>

                <Paper elevation={0} sx={{ p: 2, borderRadius: '12px', bgcolor: '#F8FAFC', border: '1px solid rgba(0,0,0,0.05)' }}>
                  <Typography variant="body2" fontWeight={700} mb={1}>Features</Typography>
                  {isUpgrade ? (
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CheckCircleIcon sx={{ fontSize: 12, color: 'success.main' }} /> Chatbot Customization
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CheckCircleIcon sx={{ fontSize: 12, color: 'success.main' }} /> More Credits
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Standard features will apply.
                    </Typography>
                  )}
                </Paper>
              </Box>

              <Box mt={4} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <InfoOutlinedIcon sx={{ fontSize: 18, color: 'primary.main', mt: 0.2 }} />
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                  Your {newPlan?.plan_name} plan starts immediately. Unused {currentPlan?.plan_name} credits will be replaced with new {newPlan?.plan_name} credits for the new cycle.
                </Typography>
              </Box>
            </Box>

            {/* Right Column: Billing (40%) */}
            <Box sx={{ flex: '0.8', p: 3, bgcolor: '#FBFBFE' }}>
              <Typography variant="overline" color="text.secondary" fontWeight={700} sx={{ mb: 2, display: 'block', letterSpacing: '1px' }}>
                BILLING SUMMARY
              </Typography>

              <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', bgcolor: 'white', border: '1px solid #EDEDF5' }}>
                <Box display="flex" justifyContent="space-between" mb={1.5}>
                  <Typography variant="body2" color="text.secondary">{newPlan?.plan_name} Plan (Total Recurring)</Typography>
                  <Typography variant="body2" fontWeight={600}>${(newPlan?.plan_amount * numLocations)?.toFixed(2)}</Typography>
                </Box>

                <Box display="flex" justifyContent="space-between" mb={1.5}>
                  <Typography variant="body2" color="text.secondary">Prorated Credit</Typography>
                  <Typography variant="body2" fontWeight={600} color="success.600">
                    -${(Math.abs(prorationData?.cash?.remaining_old_value || prorationData?.cash?.old_prorated_value || 0)).toFixed(2)}
                  </Typography>
                </Box>


                <Divider sx={{ my: 2, borderStyle: 'dashed' }} />

                <Box display="flex" justifyContent="space-between" mb={0.5} alignItems="center">
                  <Typography variant="subtitle1" fontWeight={700}>{isRefund ? "Refund Total" : "Due Today"}</Typography>
                  <Typography variant="h5" fontWeight={800} color={isRefund ? "success.main" : "primary.main"}>
                    ${Math.abs(prorationAmountCents / 100).toFixed(2)}
                  </Typography>
                </Box>

              </Paper>

              <Box sx={{ mt: 4 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1.5}>
                  SUBSCRIPTION TERMS
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">Next Billing Date</Typography>
                    <Typography variant="caption" fontWeight={600}>
                      {new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">Total Recurring Amount</Typography>
                    <Typography variant="caption" fontWeight={600}>${(newPlan?.plan_amount * numLocations)?.toFixed(2)}/mo</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      {!isProcessing && (
        <DialogActions sx={{ p: 2.5, bgcolor: '#F1F5F9', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
          <Button
            onClick={onClose}
            variant="text"
            sx={{ color: 'text.secondary', fontWeight: 600 }}
          >
            {isSuccess ? "Close" : "Go Back"}
          </Button>
          {!isSuccess && (
            <Button
              variant="contained"
              size="large"
              disabled={loadingProration || !prorationData || !!fetchError}
              onClick={handleConfirmAction}
              sx={{
                minWidth: 180,
                borderRadius: '10px',
                bgcolor: '#605DFF',
                '&:hover': { bgcolor: '#4F46E5' },
                boxShadow: '0 4px 14px 0 rgba(96, 93, 255, 0.39)',
                fontWeight: 700,
                textTransform: 'none',
                fontSize: '15px'
              }}
            >
              {loadingProration ? "Calculating..." :
                isRefund ? `Confirm Downgrade & Refund $${Math.abs(prorationAmountCents / 100).toFixed(2)}` :
                  (prorationAmountCents === 0 ? "Switch Plan Now" : `Pay $${Math.abs(prorationAmountCents / 100).toFixed(2)} & ${isUpgrade ? 'Upgrade' : 'Downgrade'}`)
              }
            </Button>

          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default PlanChangeModal;
